import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  GridOn as GridIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

export interface BoardPaymentStatusProps {
  pricePerSquare: number;
  totalCells: number;
  claimedCells: number;
  totalPaid: number;
  boardStatus: string;
  profitPercent: number; // Organizer profit percentage (0-50)
  isStandardMode: boolean; // true if standard 100-cell mode
}

export const BoardPaymentStatus: React.FC<BoardPaymentStatusProps> = ({
  pricePerSquare,
  totalCells,
  claimedCells,
  totalPaid,
  boardStatus,
  profitPercent,
  isStandardMode: _isStandardMode, // Unused - always use "Squares" terminology now
}) => {
  const cellLabel = 'Squares'; // Always use "Squares" terminology regardless of cell mode
  const cellLabelLower = 'squares';
  const totalSquares = totalCells; // Alias for backward compatibility in calculations
  const claimedSquares = claimedCells;
  const totalPotExpected = totalSquares * pricePerSquare;
  const organizerProfit = totalPotExpected * (profitPercent / 100);
  const payablePot = totalPotExpected - organizerProfit; // What goes to winners
  const claimedValue = claimedSquares * pricePerSquare;
  const unclaimedSquares = totalSquares - claimedSquares;
  const unclaimedValue = unclaimedSquares * pricePerSquare;
  const outstanding = Math.max(0, claimedValue - totalPaid);

  // Calculate percentages for squares
  const claimedPercent = (claimedSquares / totalSquares) * 100;

  // Calculate percentages relative to total pot for 3-segment bar
  const paidPercent = totalPotExpected > 0 ? (totalPaid / totalPotExpected) * 100 : 0;
  const outstandingPercent = totalPotExpected > 0 ? (outstanding / totalPotExpected) * 100 : 0;
  // Remaining is automatically shown as the grey background

  // Determine overall status
  const isFullyClaimed = claimedSquares === totalSquares;
  const isFullyPaid = totalPaid >= claimedValue && claimedValue > 0;
  const hasUnclaimedSquares = unclaimedSquares > 0;
  const hasOutstandingPayments = outstanding > 0;

  // Status color
  const getStatusColor = () => {
    if (isFullyClaimed && isFullyPaid) return 'success';
    if (hasOutstandingPayments) return 'warning';
    return 'info';
  };

  const getStatusMessage = () => {
    if (boardStatus === 'draft') {
      return 'Board is in draft mode. Publish to start accepting players.';
    }
    if (isFullyClaimed && isFullyPaid) {
      return `All ${cellLabelLower} claimed and fully paid. Ready to go!`;
    }
    if (hasUnclaimedSquares && !hasOutstandingPayments) {
      return `${unclaimedSquares} ${cellLabelLower} still available. Share your invite code!`;
    }
    if (hasOutstandingPayments && isFullyClaimed) {
      return `All ${cellLabelLower} claimed. $${outstanding.toFixed(2)} in payments still outstanding.`;
    }
    if (hasOutstandingPayments) {
      return `${unclaimedSquares} ${cellLabelLower} available. $${outstanding.toFixed(2)} in payments outstanding.`;
    }
    return 'Board is ready for the game!';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        border: 2,
        borderColor: `${getStatusColor()}.main`,
        bgcolor: `${getStatusColor()}.50`,
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridIcon fontSize="small" />
        Board Payment Status
      </Typography>

      {/* Cells/Squares Progress */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {cellLabel} Claimed
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {claimedSquares} / {totalSquares} ({claimedPercent.toFixed(0)}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={claimedPercent}
          sx={{
            height: 8,
            borderRadius: 1,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: isFullyClaimed ? 'success.main' : 'primary.main',
            }
          }}
        />
        {unclaimedSquares > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {unclaimedSquares} {cellLabelLower} unclaimed (${unclaimedValue.toFixed(2)} potential)
          </Typography>
        )}
      </Box>

      {/* Payments Progress - 3 segment bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Pot Progress
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              ${totalPaid.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" fontWeight="bold" color="warning.main">
              ${outstanding.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" fontWeight="bold" color="text.secondary">
              ${totalPotExpected.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        {/* 3-segment progress bar */}
        <Box sx={{
          height: 10,
          borderRadius: 1,
          bgcolor: 'grey.200',
          display: 'flex',
          overflow: 'hidden',
        }}>
          {/* Paid segment (green) */}
          {paidPercent > 0 && (
            <Box sx={{
              width: `${Math.min(paidPercent, 100)}%`,
              bgcolor: 'success.main',
              transition: 'width 0.3s ease',
            }} />
          )}
          {/* Outstanding segment (orange/warning) */}
          {outstandingPercent > 0 && (
            <Box sx={{
              width: `${Math.min(outstandingPercent, 100 - paidPercent)}%`,
              bgcolor: 'warning.main',
              transition: 'width 0.3s ease',
            }} />
          )}
          {/* Remaining is just the grey background */}
        </Box>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">Collected</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'grey.300' }} />
            <Typography variant="caption" color="text.secondary">Remaining</Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 2,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Total Pot
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            ${totalPotExpected.toFixed(2)}
          </Typography>
        </Box>
        {profitPercent > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Organizer ({profitPercent}%)
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="text.secondary">
              ${organizerProfit.toFixed(2)}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Payable Pot
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="primary.main">
            ${payablePot.toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Collected
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="success.main">
            ${totalPaid.toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Outstanding
          </Typography>
          <Typography variant="body1" fontWeight="bold" color={hasOutstandingPayments ? 'warning.main' : 'success.main'}>
            ${outstanding.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Status Message */}
      <Alert
        severity={getStatusColor() as 'success' | 'warning' | 'info'}
        icon={hasOutstandingPayments ? <WarningIcon /> : <MoneyIcon />}
        sx={{ mt: 2 }}
      >
        {getStatusMessage()}
      </Alert>
    </Paper>
  );
};
